/**
 * timeOfDayGrade — what colour the hour is, with no Phaser in it.
 *
 * Split out of `TimeOfDayColorGrader` so the curve can be tested. The grader
 * owns a ColorMatrix on a camera; this owns the only interesting part, which is
 * the shape of the night.
 *
 * ## The small hours
 *
 * The original curve lerped each phase toward the *next* one across that
 * phase's whole window. For dusk that is right — dusk is a journey from day to
 * night and every moment of it should be further along. For night it was
 * quietly disastrous: night's next phase is dawn, so the instant night arrived
 * the grade began travelling toward morning and never stopped.
 *
 * Measured in the running game (sky luminance sampled from the canopy gap,
 * eight points across a vigil):
 *
 *     t=0    dusk    89.5
 *     t=90   dusk    56.0
 *     t=180  night   35.7   <- the darkest moment of the entire run
 *     t=270  night   49.0
 *     t=360  night   69.5   <- six minutes in, and it looks like early evening
 *     t=420  dawn    85.9
 *     t=540  dawn   110.6
 *
 * The scene was at its darkest three minutes into a ten-minute night and
 * brightened monotonically for the remaining seven. A game named for the
 * deepest part of the night did not have one, and — since Phase 4 removed the
 * HUD and left the sky as the only clock — it was telling the player "dawn is
 * coming" from minute three, deflating exactly the stretch where the fire is
 * supposed to matter most.
 *
 * The fix is a plateau with a shallow dip in it, and it is worth being precise
 * about which of those does the work. Measured after the change:
 *
 *     t=180  night  sky 34.5   frame saturation 0.201
 *     t=240  night  sky 34.6   frame saturation 0.188
 *     t=300  night  sky 34.5   frame saturation 0.170   <- the bottom
 *     t=360  night  sky 39.3   frame saturation 0.251   (was 69.5 / 0.449)
 *     t=420  dawn   sky 86.2                            (unchanged)
 *
 * **The plateau is the fix.** Sky luminance now holds flat across the night
 * instead of climbing 35.7 -> 69.5, and at two-thirds through there is 44% less
 * colour in the frame than there was. The *dip* — what DEEP_NIGHT adds on top —
 * is real but modest: about 15% of the frame's remaining saturation between
 * night's start and the bottom. Do not read the three-movement shape below as a
 * dramatic sink; it is a long steady dark with a slight further draining in the
 * middle of it.
 *
 * What deepens is colour, not brightness. Luminance was already tuned against
 * black-crush — an earlier pass found 11 of 51 sampled background points at
 * flat black and raised night's brightness from 0.28 to 0.62 — and there is no
 * headroom left, because this grade stacks on a NightMask that is at full
 * strength through exactly this stretch. Light goes last; the blue goes first.
 */
import type { TimeOfDay } from '@game/shared';

/** Per-phase ColorMatrix parameters. */
export interface ColorGradePreset {
  /** Hue rotation in degrees (positive = warmer shift). */
  hueRotate: number;
  /** Saturation delta: positive boosts, negative desaturates. */
  saturation: number;
  /** Brightness multiplier (0..2, 1 = neutral). */
  brightness: number;
  /** Contrast delta (0 = neutral). */
  contrast: number;
}

export const GRADE: Record<TimeOfDay, ColorGradePreset> = {
  dawn:  { hueRotate:   9, saturation:  0.30, brightness: 0.75, contrast:  0.05 },
  day:   { hueRotate:   0, saturation:  0.00, brightness: 1.00, contrast:  0.00 },
  dusk:  { hueRotate: -11, saturation:  0.40, brightness: 0.88, contrast:  0.10 },
  // Brightness was 0.28. That made night twice over: the grader crushed the
  // whole frame and NightMask then darkened it again on top, so a plate whose
  // arithmetic floors the background near luminance 18 measured 9.7 — the
  // grader was multiplying the mask's own floor down with everything else, and
  // 11 of 51 sampled background points went to flat black.
  //
  // One system owns night darkness now, and it is the mask, because the mask is
  // DIRECTIONAL — dark at the treeline, lit at the hearth. This keeps the hue
  // rotation and the desaturation, which is the part it does better.
  night: { hueRotate: -54, saturation: -0.55, brightness: 0.62, contrast: -0.05 },
};

/**
 * The bottom of the night. Not the bottom of the *brightness* — that stays
 * close to the night preset, because luminance here is already as low as it can
 * go without deleting the painted mid and far ground the scene exists to be
 * looked at. What sinks is the colour: further round toward blue, and most of
 * the remaining saturation gone.
 *
 * Contrast drops too. Late at night edges stop being crisp, and flattening it
 * is what stops the desaturation reading as a grey filter laid over the frame
 * rather than as an absence of light.
 */
export const DEEP_NIGHT: ColorGradePreset = {
  hueRotate: -68,
  saturation: -0.78,
  // Identical to night's, and it must stay that way. The first attempt at this
  // preset said in its own comment that the depth came from colour rather than
  // brightness and then dropped this to 0.55 regardless — which put 13 of 51
  // sampled background points below luminance 5, worse than the 11 of 51 that
  // got night's brightness raised from 0.28 in the first place. The grade
  // stacks on NightMask, and the mask is at full strength through exactly this
  // stretch; there is no headroom here at all.
  brightness: 0.62,
  // Lowering contrast pulls values toward the middle, so it deepens the look
  // by flattening it without taking anything else down to black.
  contrast: -0.12,
};

/** Next-phase look-ahead for smooth cross-fade. */
export const NEXT_PHASE: Record<TimeOfDay, TimeOfDay> = {
  dawn: 'day', day: 'dusk', dusk: 'night', night: 'dawn',
};

/**
 * How far into the night the deep middle sits, 0..1 of the night window.
 *
 * The night window is 240s of a 420s dark stretch, starting at 180s, so 0.5
 * lands the bottom at 300s — 71% of the way through the dark. That is where
 * "the small hours" belongs: past the middle, well before any light.
 */
export const DEEP_NIGHT_AT = 0.5;

/**
 * How long the bottom is held before the climb to dawn begins, as a fraction of
 * the night window. Without a hold the arc is a V and the bottom is a single
 * instant nobody is looking at; the small hours have to be somewhere you *are*
 * for a while, not a point you pass through.
 */
export const DEEP_NIGHT_HOLD = 0.22;

function lerpPreset(a: ColorGradePreset, b: ColorGradePreset, t: number): ColorGradePreset {
  // Exact at the ends. `a + (b - a) * 1` is not bit-identical to `b` in floats,
  // and the end of one phase is the start of the next — a grade that lands a
  // rounding error away from the next phase's preset snaps by that much on the
  // handover frame. Tiny, but it is a snap in the sky, and the sky is the clock.
  if (t <= 0) return { ...a };
  if (t >= 1) return { ...b };
  return {
    hueRotate: a.hueRotate + (b.hueRotate - a.hueRotate) * t,
    saturation: a.saturation + (b.saturation - a.saturation) * t,
    brightness: a.brightness + (b.brightness - a.brightness) * t,
    contrast: a.contrast + (b.contrast - a.contrast) * t,
  };
}

/** Ease-in-out, so no segment of the night starts or stops with a corner. */
function smooth(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

/**
 * The grade at `progress` (0..1) through `phase`.
 *
 * Every phase but night is a straight cross-fade to the next one. Night is
 * three movements: down into the deep, a hold there, then out to dawn.
 */
export function gradeAt(phase: TimeOfDay, progress: number): ColorGradePreset {
  const p = progress < 0 ? 0 : progress > 1 ? 1 : progress;

  if (phase !== 'night') {
    return lerpPreset(GRADE[phase], GRADE[NEXT_PHASE[phase]], p);
  }

  if (p <= DEEP_NIGHT_AT) {
    return lerpPreset(GRADE.night, DEEP_NIGHT, smooth(p / DEEP_NIGHT_AT));
  }

  const holdUntil = DEEP_NIGHT_AT + DEEP_NIGHT_HOLD;
  if (p <= holdUntil) return { ...DEEP_NIGHT };

  return lerpPreset(DEEP_NIGHT, GRADE.dawn, smooth((p - holdUntil) / (1 - holdUntil)));
}

/** Vignette strength per phase — stronger at night/dusk for mood. */
export const VIGNETTE_STRENGTH: Record<TimeOfDay, number> = {
  dawn:  0.3,
  day:   0.15,
  dusk:  0.35,
  night: 0.55,
};

/**
 * Vignette at a moment.
 *
 * Night HOLDS its edge rather than deepening it, and then opens with the light.
 * See the note in the night branch for why: deepening it was measurably wrong,
 * and the shader math says so even more plainly than the pixels did. Phaser's
 * vignette is `sin(d / radius * PI * strength)` with radius fixed at 0.8, so
 * the curve peaks at `d = radius / (2 * strength)`. At 0.55 that peak sits at
 * d = 0.727, just outside the 0.707 screen corner. At 0.68 it falls to d =
 * 0.588 — inside the frame — so the outer third saturates to solid black and
 * then turns over, leaving the literal corners *less* obscured than a ring
 * further in. That is the black-rectangle failure NightMask was written to
 * avoid, reintroduced through a different filter.
 */
export function vignetteAt(phase: TimeOfDay, progress: number): number {
  const p = progress < 0 ? 0 : progress > 1 ? 1 : progress;
  const from = VIGNETTE_STRENGTH[phase];
  const to = VIGNETTE_STRENGTH[NEXT_PHASE[phase]];

  if (phase !== 'night') return from + (to - from) * p;

  // Night HOLDS its vignette rather than deepening it, and that is a
  // correction. The first pass pushed the peak to 0.68 — the only number in
  // this file picked by taste instead of measurement, and it was wrong.
  // Measured at the bottom of the night, raising the vignette from 0.55 to
  // 0.68 does not merely close the edge: mean luminance across the CENTRE of
  // the frame (x 0.20-0.80) fell 40.7 to 32.7, and centre points below
  // luminance 5 went from 0 to 5. It was eating the painted mid-ground, which
  // is the exact thing an earlier session raised night's brightness from 0.28
  // to protect.
  //
  // So the deepening is carried entirely by colour, as intended all along. The
  // vignette's job here is only to stop *opening* — the old linear ramp
  // travelled 0.55 to dawn's 0.30 across the whole night, widening the frame
  // through the small hours for the same reason the colour was brightening.
  const holdUntil = DEEP_NIGHT_AT + DEEP_NIGHT_HOLD;
  if (p <= holdUntil) return from;
  return from + (to - from) * smooth((p - holdUntil) / (1 - holdUntil));
}
